import { z, ZodObject } from 'zod';
import {
  QuestionStructureType,
  QuestionType,
  SurveyExtensionPoint,
  SurveyInputMode,
  SurveyStatus,
} from '../../enums';
import { getTranslationValidation } from '../../helpers';
import { SurveyQuestionRole } from '../../enums/survey';
import { TranslationValidatorSchema } from './translation.validator';

const StructuredAnswerItemValidatorSchema = z.object({
  name: z.string(),
  // A structured field the fan left blank is submitted as null.
  value: z.union([z.string().min(1), z.boolean()]).nullable(),
});

export function generateRandomStructuredFieldsValidatorSchema(
  fields?: {
    name: string;
    isMandatory: boolean;
    type: QuestionStructureType;
  }[]
): ZodObject {
  let schema = z.object({});
  if (!fields) return schema;
  fields.forEach((field) => {
    const isFreeText = field.type === QuestionStructureType.FREE_TEXT;

    if (field.isMandatory) {
      schema = z.object({
        ...schema.shape,
        [field.name]: isFreeText
          ? z.string().min(1)
          : z.union([z.string().min(1), z.boolean()]),
      });
    } else {
      schema = z.object({
        ...schema.shape,
        [field.name]: isFreeText
          ? z.string().nullable().optional()
          : z.union([z.string(), z.boolean()]).nullable().optional(),
      });
    }
  });
  return schema;
}

export function generateRandomRegularFieldsValidatorSchema(
  fields?: {
    name: string;
    isMandatory: boolean;
    type: QuestionType;
  }[]
): ZodObject {
  let schema = z.object({});
  if (!fields) return schema;

  fields.forEach((field) => {
    if (field.isMandatory) {
      schema = schema.extend({
        [field.name]: z
          .object({
            offeredAnswerId: z
              .union([z.string().nullable(), z.array(z.string()).nullable()])
              .optional(),
            answerText: z.string().nullable().optional(),
          })
          .refine(
            (data) =>
              data.offeredAnswerId ||
              (Array.isArray(data.offeredAnswerId) &&
                data.offeredAnswerId.length > 0) ||
              (data.answerText && data.answerText.trim().length > 0),
            {
              message: 'Either offeredAnswerId or answerText is required.',
              path: [field.name],
            }
          ),
      });
    } else {
      schema = schema.extend({
        [field.name]: z
          .object({
            offeredAnswerId: z
              .union([z.string(), z.array(z.string())])
              .nullable()
              .optional(),
            answerText: z.string().nullable().optional(),
          })
          .nullable()
          .optional(),
      });
    }
  });

  return schema;
}

export function generateSurveyAnswersValidatorSchema(
  regularFields: {
    name: string;
    isMandatory: boolean;
    type: QuestionType;
  }[],
  structuredFields: {
    name: string;
    isMandatory: boolean;
    type: QuestionStructureType;
  }[]
): ZodObject {
  const structuredAnswerSchema =
    generateRandomStructuredFieldsValidatorSchema(structuredFields);

  const regularAnswerSchema =
    generateRandomRegularFieldsValidatorSchema(regularFields);

  return z.object({
    structuredAnswers: structuredAnswerSchema.optional(),
    regularAnswers: regularAnswerSchema,
  });
}

export const SurveyAnswersValidatorSchema = z.object({
  answers: z.array(
    z.object({
      questionId: z.string(),
      surveyInstanceId: z.string().optional(),
      answers: z.array(
        z.object({
          offeredAnswerId: z
            .union([z.string(), z.array(z.string())])
            .nullable()
            .optional(),
          structuredAnswer: z
            .array(StructuredAnswerItemValidatorSchema)
            .optional(),
          answerText: z.string().nullable().optional(),
        })
      ),
    })
  ),
});

export type SurveyAnswersValidator = z.infer<
  typeof SurveyAnswersValidatorSchema
>;

export const SurveyValidatorSchema = z.object({
  name: z.string(),
  description: z.array(TranslationValidatorSchema),
  title: z.array(TranslationValidatorSchema),
  status: z.enum(SurveyStatus),
  surveyQuestions: z
    .array(
      z.object({
        mandatory: z.boolean(),
        enabled: z.boolean(),
        questionId: z.string(),
        /** Insights metric this question feeds in this survey. Omitted means none. */
        role: z.enum(SurveyQuestionRole).optional().nullable(),
        /** Which choice means yes, for a role whose figure is a rate. Set with the role, or null. */
        affirmativeChoiceId: z.string().optional().nullable(),
      })
    )
    .optional(),
  /** Optional company ID for company-scoped surveys. NULL/undefined means global survey. */
  companyId: z.string().optional().nullable(),
});

export type SurveyValidator = z.infer<typeof SurveyValidatorSchema>;

export const SurveyUpdateValidatorSchema = SurveyValidatorSchema.partial();

export type SurveyUpdateValidator = z.infer<typeof SurveyUpdateValidatorSchema>;

export const SurveyFormValidatorSchema = z.object({
  name: z.string().min(1),
  description: getTranslationValidation(),
  title: getTranslationValidation(),
  status: z.enum(SurveyStatus),
});

export type SurveyFormValidator = z.infer<typeof SurveyFormValidatorSchema>;

export const SurveyInstanceValidatorSchema = z.object({
  surveyId: z.string().min(1, 'exp_surveys_error_survey_id_required'),
  waitinglistId: z.string().optional(),
  fangroupId: z.string().optional().nullable(),
  /**
   * Optional in form state — both survey forms initialise the scope selector
   * empty and fall back to BEFORE_JOINING_WAITINGLIST on submit. `.default()`
   * encodes exactly that: `z.input` leaves it optional, `z.output` (what the
   * submit handler receives) always carries a value.
   */
  extensionPoint: z
    .enum(SurveyExtensionPoint, {
      message: 'exp_surveys_error_invalid_extension_point',
    })
    .default(SurveyExtensionPoint.BEFORE_JOINING_WAITINGLIST),
  inputMode: z.enum(SurveyInputMode, {
    message: 'exp_surveys_error_invalid_input_mode',
  }),
});

export type SurveyInstanceValidator = z.infer<
  typeof SurveyInstanceValidatorSchema
>;
